/** @jsxImportSource @emotion/react */
import { Buttons, List } from '@zip/business-components';
import { BulletEmphasised } from '@zip/icons';
import { BasePage } from 'components';
import { useMerchantData, useSnackbar } from 'contexts';
import { PageAction } from 'enums';
import { css, isProduction, isSandbox } from 'global';
import { useFetch } from 'hooks';
import { FC, useState } from 'react';
import { PageProps } from 'types';
import { logError, logEvent } from 'utils';

import * as styles from './PosMaterialsPage.styles';

const PosMaterialsPage: FC<PageProps> = () => {
  const { get } = useFetch();
  const Snackbar = useSnackbar();
  const { merchantIdentity } = useMerchantData();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchPosMaterialUrl = async (): Promise<string | null> => {
    if (!merchantIdentity || isSandbox || isProduction) {
      return null;
    }

    setIsLoading(true);
    logEvent(PageAction.fetchPosVendorUrl, merchantIdentity);

    try {
      const res = await get('/marketing/pos-materials-url');
      if (res.status === 200) {
        const url = (await res?.json())?.url;
        return url;
      } else {
        Snackbar.error(
          'Something went wrong generating your unique link, please try again'
        );
        return null;
      }
    } catch (err) {
      logError(PageAction.fetchPosVendorUrl, err);
      Snackbar.error(
        'Something went wrong generating your unique link, please try again'
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    const url = await fetchPosMaterialUrl();

    if (url) {
      logEvent(PageAction.openPosVendorUrl);
      window.open(url);
    }
  };

  return (
    <BasePage
      title="Instore POS materials"
      prodAlert="New POS materials coming soon"
    >
      <p css={[css.noTopMrgn]}>
        As a Zip partner, we want to support your business online and instore.
        <br />
        By displaying Zip POS materials in your physical locations, customers
        know they can use Zip at checkout.
      </p>
      <p>
        Just like adding our online marketing assets to your site, having
        visibility instore drives:
        <List
          items={[
            {
              primary: 'higher conversions',
              icon: BulletEmphasised,
            },
            {
              primary: 'higher average basket sizes',
              icon: BulletEmphasised,
            },
            {
              primary: 'increased repeat transactions',
              icon: BulletEmphasised,
            },
          ]}
          css={styles.list}
        />
      </p>
      <Buttons.Primary
        loading={isLoading}
        disabled={!merchantIdentity || isSandbox || isProduction}
        onClick={handleClick}
      >
        Order now
      </Buttons.Primary>
    </BasePage>
  );
};

export default PosMaterialsPage;
